import { NavLink, Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Home, Wallet, Phone, ShoppingBag, ShieldCheck, Sparkles, Bell, Check } from "lucide-react";
import Brand from "@/components/Brand";

export default function UserLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || "";

  const loadNotifications = async () => {
    const token = localStorage.getItem("sk_token");
    if (!token) return;

    try {
      const [listRes, countRes] = await Promise.all([
        fetch(`${API}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (listRes.ok) {
        const data = await listRes.json();
        const list = Array.isArray(data) ? data : [];
        setNotifications(list);
        setUnreadCount(list.filter((item) => !item.read).length);
      }
    } catch (err) {
      console.error("Bildirimler yüklenemedi:", err);
    }
  };

  useEffect(() => {
    loadNotifications();

    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const markNotificationRead = async (notificationId) => {
    const token = localStorage.getItem("sk_token");
    if (!token) return;

    // Önce arayüzü anında güncelle
    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notificationId ? { ...item, read: true } : item
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      const res = await fetch(
        `${API}/api/notifications/${notificationId}/read`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) {
        console.error("Bildirim okundu olarak işaretlenemedi:", res.status);
        return;
      }

      // Backend ile tekrar senkronize et
      const listRes = await fetch(`${API}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (listRes.ok) {
        const data = await listRes.json();
        const list = Array.isArray(data) ? data : [];
        setNotifications(list);
        setUnreadCount(list.filter((item) => !item.read).length);
      }
    } catch (err) {
      console.error("Bildirim okunamadı:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    const token = localStorage.getItem("sk_token");
    if (!token) return;

    const unread = notifications.filter((item) => !item.read);

    // Arayüzü hemen güncelle
    setNotifications((prev) =>
      prev.map((item) => ({ ...item, read: true }))
    );
    setUnreadCount(0);

    // Backend/MongoDB'de de okundu yap
    try {
      await Promise.all(
        unread.map(async (item) => {
          const res = await fetch(
            `${API}/api/notifications/${item.id}/read`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!res.ok) {
            console.error(
              "Bildirim okunamadı:",
              item.id,
              res.status
            );
          }
        })
      );
    } catch (err) {
      console.error("Bildirimler okunurken hata:", err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `px-3 py-2 text-sm tracking-wide transition-colors ${
      isActive ? "text-white border-b-2 border-[hsl(217,91%,60%)]" : "text-white/60 hover:text-white border-b-2 border-transparent"
    }`;

  const mobileLink = ({ isActive }) =>
    `flex flex-col items-center gap-1 flex-1 py-1 text-[10px] tracking-wide transition-colors ${
      isActive ? "text-white" : "text-white/55 hover:text-white"
    }`;

  const displayName = user?.first_name || user?.username || "";

  return (
    <div className="min-h-screen brand-bg text-white flex flex-col">
      <header className="sticky top-0 z-[1000] backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-6 h-16">
          <Link to="/panel" data-testid="brand-link">
            <Brand size="sm" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/panel" end className={linkClass} data-testid="nav-dashboard">Ana Sayfa</NavLink>
            <NavLink to="/buy" className={linkClass} data-testid="nav-buy">Numara Al</NavLink>
            <NavLink to="/numbers" className={linkClass} data-testid="nav-numbers">Alınanlar</NavLink>
            <NavLink to="/deposit" className={linkClass} data-testid="nav-deposit">Bakiye</NavLink>
            <NavLink to="/sms-ai" className={linkClass} data-testid="nav-smsai">SMS AI</NavLink>
            {user?.role === "admin" && (
              <NavLink to="/yonetimvolki35" className={linkClass} data-testid="nav-admin">
                <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Admin</span>
              </NavLink>
            )}
          </nav>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">Bakiye</div>
              <div className="font-mono font-bold text-white" data-testid="user-balance">
                ₺{Number(user?.balance || 0).toFixed(2)}
              </div>
            </div>

            <div className="relative">
              <button
                type="button"
                onClick={() => {
  const next = !notificationsOpen;
  setNotificationsOpen(next);

  if (next) {
    markAllNotificationsRead();
  }
}}
                className="relative p-2.5 rounded-xl border border-white/10 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                title="Bildirimler"
                aria-label="Bildirimler"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border-2 border-black">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && createPortal(
                <div
                  className="fixed right-5 top-[76px] w-[380px] max-w-[calc(100vw-95px)] max-h-[calc(100vh-95px)] rounded-2xl border border-white/10 bg-[#101116] shadow-[0_25px_80px_rgba(0,0,0,0.75)] overflow-hidden"
                  style={{ zIndex: 2147483647 }}
                >
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">Bildirimler</div>
                      <div className="text-[10px] text-white/40 mt-0.5">
                        {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : "Tüm bildirimler okundu"}
                      </div>
                    </div>
                    <Bell className="w-4 h-4 text-white/40" />
                  </div>

                  <div className="max-h-[calc(100vh-155px)] overflow-y-auto overscroll-contain">
                    {notifications.length === 0 ? (
                      <div className="px-5 py-10 text-center text-white/40 text-sm">
                        Henüz bildiriminiz yok.
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={async () => {
                            if (!notification.read) {
                              await markNotificationRead(notification.id);
                            }
                          }}
                          className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${
                            !notification.read ? "bg-white/[0.035]" : ""
                          }`}
                        >
                          <div className="flex gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                              notification.read ? "bg-white/20" : "bg-blue-400"
                            }`} />

                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className={`text-sm ${
                                  notification.read ? "text-white/70" : "text-white font-semibold"
                                }`}>
                                  {notification.title}
                                </div>

                                {notification.read && (
                                  <Check className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                                )}
                              </div>

                              <div className="text-xs text-white/80 mt-2 leading-6 whitespace-pre-wrap break-words [overflow-wrap:anywhere] max-w-full">
                                {notification.message}
                              </div>

                              <div className="text-[9px] text-white/25 mt-2">
                                {notification.created_at
                                  ? new Date(notification.created_at).toLocaleString("tr-TR")
                                  : ""}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>,
                document.body
              )}
            </div>

            <div className="hidden md:flex items-center gap-2 pl-3 border-l border-white/10">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(217,91%,55%)] to-[hsl(0,84%,58%)] flex items-center justify-center text-white text-xs font-bold">
                {(displayName[0] || "U").toUpperCase()}
              </div>
              <span className="text-sm text-white/80">{displayName}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-white/60 hover:text-white transition-colors p-2 border border-white/10 hover:border-white/30 rounded-lg"
              title="Çıkış"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-5 md:px-6 py-8 md:py-10 pb-28 md:pb-10">{children}</main>

      <footer className="hidden md:block border-t border-white/10 py-6 text-center text-xs text-white/40 tracking-[0.2em]">
        <div className="uppercase mb-2">SmsKapıda · Sanal Numara Servisi · 20dk Otomatik İade Garantili</div>
        <div className="flex justify-center gap-4 normal-case tracking-normal">
          <Link to="/legal/terms" className="hover:text-white transition-colors">Kullanım Koşulları</Link>
          <Link to="/legal/privacy" className="hover:text-white transition-colors">Gizlilik Politikası</Link>
          <Link to="/legal/kvkk" className="hover:text-white transition-colors">KVKK</Link>
          <Link to="/legal/refund" className="hover:text-white transition-colors">İade Politikası</Link>
        </div>
      </footer>

      {/* Mobile bottom nav — 5 items */}
      <nav className="mobile-nav fixed bottom-3 left-3 right-3 z-40 backdrop-blur-2xl bg-black/70 border border-white/10 rounded-2xl px-1 py-1 flex items-center justify-around shadow-2xl">
        <NavLink to="/panel" end className={mobileLink} data-testid="mnav-home">
          <Home className="w-5 h-5" /><span>Ana Sayfa</span>
        </NavLink>
        <NavLink to="/deposit" className={mobileLink} data-testid="mnav-balance">
          <Wallet className="w-5 h-5" /><span>Bakiye</span>
        </NavLink>
        <NavLink to="/sms-ai" className={mobileLink} data-testid="mnav-smsai">
          <Sparkles className="w-5 h-5" /><span>SMS AI</span>
        </NavLink>
        <NavLink to="/numbers" className={mobileLink} data-testid="mnav-received">
          <Phone className="w-5 h-5" /><span>Alınanlar</span>
        </NavLink>
        <NavLink to="/buy" className={mobileLink} data-testid="mnav-buy">
          <ShoppingBag className="w-5 h-5" /><span>Numara Al</span>
        </NavLink>
      </nav>
    </div>
  );
}
